import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Sparkles, Boxes, Check, Inbox as InboxIcon, ShieldCheck, Upload } from "lucide-react";

const SAMPLE = `Bonjour,
Merci pour votre achat chez Boulanger le 12/04/2026.
Article : Cafetière Nespresso Vertuo Next — Réf VNEXT-2026 — 149,00 €
Garantie constructeur : 24 mois.
Total payé : 149,00 € TTC.`;

export default function Inbox() {
  const { user, refreshUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({ sender: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [alias, setAlias] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/inbox").then((r) => setMessages(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.omy_inbox) setAlias(user.omy_inbox.split("@")[0]); }, [user]);

  const saveAlias = async () => {
    if (alias.trim().length < 3) return toast.error("Alias trop court (3 min)");
    try { const { data } = await api.post("/inbox/alias", { handle: alias.trim() }); toast.success(`Alias : ${data.omy_inbox}`); await refreshUser(); }
    catch (e) { toast.error(e.response?.data?.detail || "Alias indisponible"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/inbox/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Ticket analysé par OMY"); load(); }
    catch (err) { toast.error("Analyse impossible"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const ingest = async () => {
    if (!form.body.trim()) return toast.error("Colle le contenu d'un e-mail ou ticket");
    setBusy(true);
    try {
      await api.post("/inbox/ingest", form);
      toast.success("OMY a analysé le message");
      setForm({ sender: "", subject: "", body: "" });
      load();
    } catch (e) { toast.error("Analyse impossible"); }
    finally { setBusy(false); }
  };

  const addItems = async (mid) => {
    const created = await api.post(`/inbox/${mid}/add-items`);
    toast.success(`${created.data.length} produit(s) ajouté(s) à tes biens`);
    load();
  };

  const copyAddress = () => { navigator.clipboard?.writeText(user?.omy_inbox || ""); toast.success("Adresse copiée"); };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">OMY Inbox</h1>
        <p className="text-muted-foreground text-sm mt-1">Donne ton adresse OMY aux commerçants au lieu de ton e-mail perso. OMY reçoit le ticket, l'analyse et te propose d'ajouter les produits.</p>
      </div>

      <div className="omy-glass rounded-2xl p-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center shrink-0"><Mail size={20} className="text-accent" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">Ton adresse OMY Inbox</p>
            <p className="font-mono text-sm truncate" data-testid="inbox-address">{user?.omy_inbox}</p>
          </div>
          <Button data-testid="inbox-copy-btn" variant="ghost" onClick={copyAddress} className="text-primary shrink-0">Copier</Button>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <span className="text-xs text-muted-foreground shrink-0">Personnaliser :</span>
          <Input data-testid="inbox-alias-input" value={alias} onChange={(e) => setAlias(e.target.value)} className="bg-black/30 h-9 flex-1 min-w-0" placeholder="jeremy" />
          <span className="text-xs text-muted-foreground hidden sm:inline shrink-0">@omyemail.com</span>
          <Button data-testid="inbox-alias-save-btn" onClick={saveAlias} className="bg-primary hover:bg-primary/90 rounded-full h-9 shrink-0">OK</Button>
        </div>
      </div>

      <div className="omy-glass rounded-2xl p-6 space-y-3">
        <h2 className="font-display font-medium flex items-center gap-2"><Sparkles size={18} className="text-primary" /> Simuler la réception d'un e-mail / ticket</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Expéditeur</Label><Input data-testid="inbox-sender-input" value={form.sender} onChange={(e) => setForm({ ...form, sender: e.target.value })} className="mt-1 bg-black/30" placeholder="caisse@boulanger.com" /></div>
          <div><Label>Objet</Label><Input data-testid="inbox-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 bg-black/30" placeholder="Votre ticket de caisse" /></div>
        </div>
        <div><Label>Contenu</Label><Textarea data-testid="inbox-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} className="mt-1 bg-black/30" placeholder="Colle ici le contenu de l'e-mail ou du ticket…" /></div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" data-testid="inbox-file-input" onChange={onFile} />
          <Button data-testid="inbox-upload-btn" variant="ghost" onClick={() => fileRef.current?.click()} disabled={busy}><Upload size={16} className="mr-1.5" /> Photo / Fichier</Button>
          <Button data-testid="inbox-sample-btn" variant="ghost" onClick={() => setForm({ sender: "caisse@boulanger.com", subject: "Votre ticket Boulanger", body: SAMPLE })}>Exemple</Button>
          <Button data-testid="inbox-ingest-btn" onClick={ingest} disabled={busy} className="bg-primary hover:bg-primary/90 rounded-full">{busy ? "Analyse…" : "Analyser avec OMY"}</Button>
        </div>
      </div>

      <div className="space-y-4">
        {messages.length === 0 && <p className="text-muted-foreground text-sm flex items-center gap-2"><InboxIcon size={16} /> Aucun message reçu.</p>}
        {messages.map((m, i) => (
          <div key={m.id} data-testid={`inbox-msg-${i}`} className="omy-glass rounded-2xl p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{m.subject || "(sans objet)"}</p>
                <p className="text-xs text-muted-foreground truncate">{m.sender} {m.merchant && `· ${m.merchant}`}</p>
              </div>
              {m.status === "processed" && <span className="text-xs text-emerald-400 flex items-center gap-1 shrink-0"><Check size={12} /> Ajouté</span>}
            </div>
            {m.summary && <p className="text-sm text-muted-foreground mt-2">{m.summary}</p>}
            {m.products?.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {m.products.map((p, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm rounded-lg bg-black/20 px-3 py-2">
                    <Boxes size={14} className="text-primary shrink-0" />
                    <span className="flex-1">{p.name} {p.brand && <span className="text-muted-foreground">· {p.brand}</span>}</span>
                    {p.price != null && <span className="text-xs text-muted-foreground">{p.price} €</span>}
                    {p.warranty_months && <span className="text-xs text-muted-foreground">· {p.warranty_months} mois</span>}
                  </div>
                ))}
              </div>
            )}
            {m.products?.length > 0 && m.status !== "processed" && (
              <Button data-testid={`inbox-add-items-${i}`} onClick={() => addItems(m.id)} className="mt-3 bg-primary hover:bg-primary/90 rounded-full">
                <Check size={16} className="mr-1.5" /> Ajouter {m.products.length} produit(s) à mes biens
              </Button>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck size={12} className="text-accent" /> Anti-spam : seuls les messages que tu importes sont analysés. Le branchement e-mail réel se fait via les DNS du domaine.</p>
    </div>
  );
}
